import {useEffect, useMemo} from 'react'
import type {Thread} from '@github-ui/diff-lines/conversation-types'
import {useMarkersActions} from '../stores/selectors'
import {
  positionedThreadToSubject,
  useThreadPreviewPositioning,
} from '../page-data/loaders/use-thread-preview-positioning'

const POSITIONING_PAGE_SIZE = 40

/**
 * Fetches complete server-provided subjects for threads that do not have one.
 *
 * @param threadPreviews - Array of threads to process
 */
export function useHydrateThreadPositioning(threadPreviews: Thread[]) {
  const {updateThreads} = useMarkersActions()

  const threadsNeedingPositioning = useMemo(
    () =>
      threadPreviews
        .filter(thread => !thread.subject?.diffLines)
        .map(thread => Number(thread.id))
        .slice(0, POSITIONING_PAGE_SIZE),
    [threadPreviews],
  )

  const {data: positioningData} = useThreadPreviewPositioning(threadsNeedingPositioning)

  useEffect(() => {
    if (!positioningData || Object.keys(positioningData.threads).length === 0) return

    updateThreads(threads => {
      return threads.map(thread => {
        const positioning = positioningData.threads[thread.id]
        if (!positioning) return thread

        return {
          ...thread,
          path: positioning.path ?? thread.path,
          originalDiffPathUri: positioning.originalDiffPathUri ?? null,
          isOutdated: positioning.isOutdated ?? thread.isOutdated,
          subject: positionedThreadToSubject(positioning),
        }
      })
    })
  }, [positioningData, updateThreads])
}
